import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import Button from '../../../../../ui/canon/Button';
import { SendIcon, SparklesIcon, HelpCircleIcon, CheckIcon } from 'lucide-react';

const NumberInputWidget = ({
  onAnswer,
  disabled,
  placeholder = 'Enter amount...',
  suffix,
  onHelp,
  aiSuggestion,
  initialValue = '',
}) => {
  const intl = useIntl();
  const [value, setValue] = useState(initialValue ? String(initialValue) : '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(!!aiSuggestion);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    setValue(initialValue ? String(initialValue) : '');
    setIsSubmitting(false);
  }, [initialValue]);

  const handleSubmit = () => {
    if (value.trim() && !isSubmitting) {
      setIsSubmitting(true);
      // Removed formatValue logic here to keep data clean, formatting should happen on display
      // But if suffix implies unit, we might want to send { value: 123, unit: 'years' } or similar
      // For now, adhering to the prototype which sends string string with suffix if I recall correctly
      // Prototype: const formattedValue = suffix ? `${value}${suffix}` : value;
      // FindBob backend probably expects structured data. I will send object { value: 123, formatted: "123 years" }
      // Or just value if it's a number field.
      // Let's send the raw value and let backend/display handle suffix.
      // Wait, prototype sends formatted string.
      // "onAnswer(formattedValue);"
      // I'll send object: { value: value, formatted: suffix ? `${value}${suffix}` : value }
      
      const formattedValue = suffix ? `${value}${suffix}` : value;
      onAnswer({ value: value, formatted: formattedValue });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleUseSuggestion = () => {
    if (aiSuggestion) {
      setValue(aiSuggestion);
      setIsFadingOut(true);
      setTimeout(() => {
        setShowSuggestion(false);
        setIsFadingOut(false);
      }, 300);
    }
  };
  
  const inputClasses = 'flex-1 bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 py-1.5';

  return (
    <div className="space-y-3">
      {/* AI Helper Button */}
      {onHelp && (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onHelp}
            disabled={disabled || isSubmitting}
            className="gap-2"
          >
            <HelpCircleIcon className="h-3.5 w-3.5" />
            {intl.formatMessage({ id: 'succession.plans.widget_help_button' })}
          </Button>
        </div>
      )}

      {/* AI Suggestion */}
      {showSuggestion && aiSuggestion && (
        <div
          className={`bg-succession-primary/5 border border-succession-primary/20 rounded-lg p-4 space-y-3 transition-opacity duration-300 ${
            isFadingOut ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div className="flex items-start gap-3">
            <SparklesIcon className="h-4 w-4 text-succession-primary flex-shrink-0 mt-0.5" />

            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.plans.ai_suggestion_label' })}
              </p>
              <p className="text-sm text-succession-text-secondary">{aiSuggestion}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleUseSuggestion}
              className="gap-2"
            >
              <CheckIcon className="h-3.5 w-3.5" />
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_use' })}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSuggestion(false)}
            >
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_dismiss' })}
            </Button>
          </div>
        </div>
      )}

      {/* Input Field */}
      <div className="flex gap-2">
        <input
          type="text" // using text to allow loose input before validation if needed, or number
          inputMode="numeric"
          pattern="[0-9]*"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isSubmitting}
          className={inputClasses}
        />

        {suffix && (
          <div className="flex items-center px-4 py-1.5 border border-succession-border rounded-md bg-succession-bg-secondary text-succession-text-muted">
            {suffix}
          </div>
        )}
      </div>
      <Button
        onClick={handleSubmit}
        disabled={!value.trim() || disabled || isSubmitting}
        className="w-full"
      >
        <SendIcon className="h-4 w-4 mr-2" />
        {intl.formatMessage({ id: 'succession.plans.widget_submit_answer' })}
      </Button>
    </div>
  );
};

export default NumberInputWidget;
